import React, { useMemo } from "react";
import { Popup, Icon } from "semantic-ui-react";
import moment from "moment";
import _ from "lodash";
import { formatValueAsCurrency } from "../../components/shared/utils";
import { RestrictionTypesValues, RestrictionTypes } from "../../components/shared/constants";
import TextList from "../../components/shared/TextList";
import { buildAutoDescriptionForPolicy } from "../../components/shared/reservationPolicy";
import AvailabilityCenter from "./AvailabilityCenter";

export const isLightColor = (hex) => {
    if (!hex) return false;
    const c = hex.replace('#', '');
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 160;
};

export const RESTRICTION_COLORS = {
    red: '#db2828',
    yellow: '#fbbd08',
    blue: '#2185d0',
    teal: '#00b5ad',
    green: '#8bc34a',
    orange: '#f2711c',
};

// OCCROOMS-1 perf: bucket per-date arrays once per `data` reference into Maps keyed
// by ISO yyyy-mm-dd. Avoids the prior per-cell linear .find/.filter scans (each
// running a moment(...).isSame compare) which produced O(cells * rows) moment
// allocations and made React.memo's shallow compare meaningless.
const isoKey = (d) => moment(d).format('YYYY-MM-DD');

const buildDateBuckets = (data) => {
    const buckets = {
        yieldByDate: new Map(),
        availabilityByDate: new Map(),
        overbookingByDate: new Map(),
        productsByDate: new Map(),
        restrictionsByDate: new Map(),
        reservationPoliciesByDate: new Map(),
        hotelParameterByDate: new Map(),
        forecastByDate: new Map(),
        hotelAvailabilityGIByDate: new Map(),
    };
    if (!data) return buckets;
    (data.Yield || []).forEach(x => buckets.yieldByDate.set(isoKey(x.Date), x));
    (data.Availability || []).forEach(x => {
        const k = isoKey(x.Date);
        const arr = buckets.availabilityByDate.get(k);
        if (arr) arr.push(x); else buckets.availabilityByDate.set(k, [x]);
    });
    (data.Overbooking || []).forEach(x => buckets.overbookingByDate.set(isoKey(x.Date), x));
    (data.Products || []).forEach(x => {
        const k = isoKey(x.Date);
        if (!buckets.productsByDate.has(k)) buckets.productsByDate.set(k, x);
    });
    (data.Restrictions || []).forEach(x => {
        const k = isoKey(x.Date);
        if (!buckets.restrictionsByDate.has(k)) buckets.restrictionsByDate.set(k, x);
    });
    (data.ReservationPolicies || []).forEach(x => {
        const k = isoKey(x.Date);
        if (!buckets.reservationPoliciesByDate.has(k)) buckets.reservationPoliciesByDate.set(k, x);
    });
    (data.HotelParameters || []).forEach(x => buckets.hotelParameterByDate.set(isoKey(x.Date), x));
    (data.Forecast || []).forEach(x => {
        const k = isoKey(x.Date);
        if (!buckets.forecastByDate.has(k)) buckets.forecastByDate.set(k, x);
    });
    (data.HotelAvailability || []).forEach(x => {
        const k = isoKey(x.Date);
        const sum = buckets.hotelAvailabilityGIByDate.get(k) || 0;
        buckets.hotelAvailabilityGIByDate.set(k, sum + (x.GrossInventory ?? 0));
    });
    return buckets;
};

// Compute event spans once per HotelParameters reference: a single chronological
// pass produces { eventId -> { centerDateKey } }, eliminating the per-cell 45x45
// quadratic walk that previously recomputed the same center on every event cell.
const buildEventSpans = (hotelParameters) => {
    const map = new Map();
    if (!hotelParameters || hotelParameters.length === 0) return map;
    // Sort a copy by date ascending to walk in order regardless of payload order.
    const sorted = [...hotelParameters]
        .filter(p => p && p.CalendarEventId)
        .sort((a, b) => new Date(a.Date) - new Date(b.Date));
    let i = 0;
    while (i < sorted.length) {
        const eventId = sorted[i].CalendarEventId;
        const runStart = i;
        while (i + 1 < sorted.length && sorted[i + 1].CalendarEventId === eventId) {
            // Require strict day-by-day contiguity to count as same span.
            const prevDate = moment(sorted[i].Date);
            const nextDate = moment(sorted[i + 1].Date);
            if (nextDate.diff(prevDate, 'days') !== 1) break;
            i++;
        }
        const runEnd = i;
        const totalDays = runEnd - runStart + 1;
        const centerIndex = Math.floor(totalDays / 2);
        const centerKey = isoKey(sorted[runStart + centerIndex].Date);
        // If the same event appears in multiple non-contiguous runs, keep the
        // first center we see; downstream we still flag every cell whose ISO key
        // matches a center, so this remains visually correct for the common case
        // of a single contiguous span.
        if (!map.has(eventId)) {
            map.set(eventId, { centerKey });
        }
        i++;
    }
    return map;
};

// Exported for the parent so buckets are computed ONCE per `data` change rather
// than once per visible cell. Also used internally as a fallback when DayCell is
// rendered standalone (e.g., from tests) without precomputed buckets.
export const useDayCellBuckets = (data) => useMemo(() => buildDateBuckets(data), [data]);
export const useEventSpans = (hotelParameters) => useMemo(() => buildEventSpans(hotelParameters), [hotelParameters]);

// Same OCCROOMS-1 perf fix as buildDateBuckets, applied to allocationDates: bucket
// once per (allocationDates, allocationIds) change into a Map keyed by ISO date,
// pre-filtered to the selected allocations, instead of DayCell re-scanning the raw
// array with moment(...).isSame + .includes() on every cell render.
const buildAllocationDateBuckets = (allocationDates, allocationIds) => {
    const map = new Map();
    (allocationDates || []).forEach(d => {
        if (!allocationIds?.includes(d.AllocationId)) return;
        const k = isoKey(d.Date);
        const arr = map.get(k);
        if (arr) arr.push(d); else map.set(k, [d]);
    });
    return map;
};
export const useAllocationDateBuckets = (allocationDates, allocationIds) =>
    useMemo(() => buildAllocationDateBuckets(allocationDates, allocationIds), [allocationDates, allocationIds]);

// OCCROOMS-1: when no RoomType filter is applied the BFF returns one row per
// (RoomType, Date) — aggregate across room types so the cell reflects the
// hotel-level totals required by FR-6. Sums components (Sold/Ooo/Oos/GI/Available)
// rather than pre-filtering rows so OOS-only RTs are not dropped.
//
// Hoisted to module scope (pure function, no closure over component state) so
// there is one copy per module rather than one per DayCell instance. The reduce
// callback mutates a single accumulator object in place to avoid allocating a
// new object per row; callers must not retain the reference across calls.
const aggregateAvailability = (rows) => {
    if (!rows || rows.length === 0) return null;
    if (rows.length === 1) return rows[0];
    // Sum the components first, then recompute hotel-level Occupancy as
    // Sold / (GI - Oos) * 100 so the per-RT % from the last row does not
    // leak into the cell value.
    const summed = rows.reduce((acc, r) => {
        acc.Date = r.Date;
        acc.GrossInventory += (r.GrossInventory ?? 0);
        acc.Sold += (r.Sold ?? 0);
        acc.Ooo += (r.Ooo ?? 0);
        acc.Oos += (r.Oos ?? 0);
        acc.Available += (r.Available ?? 0);
        if (r.AvailabilityConsideringOverbooking != null) acc.hasOB = true;
        acc.AvailabilityConsideringOverbooking += (r.AvailabilityConsideringOverbooking ?? r.Available ?? 0);
        return acc;
    }, { Date: null, GrossInventory: 0, Sold: 0, Ooo: 0, Oos: 0, Available: 0, AvailabilityConsideringOverbooking: 0, hasOB: false, Occupancy: 0 });
    if (!summed.hasOB) summed.AvailabilityConsideringOverbooking = null;
    const opInv = summed.GrossInventory - summed.Oos;
    summed.Occupancy = opInv > 0 ? (summed.Sold / opInv) * 100 : 0;
    return summed;
};

// HS-70658: Available including the configured overbooking allowance
// (PMS AvailabilityConsideringOverbooking); falls back to plain Available when absent.
// Same as Optima Live's hotel row (pages/Live/index.jsx): Sold / (Gross - OOS), one decimal.
// On purpose, a day with every room OOS shows 0.0%, where Live divides by 1 (Sold x 100%).
export const formatOccupancy = (sold, operationalInventory) =>
    (operationalInventory > 0 ? (sold / operationalInventory) * 100 : 0).toFixed(1);

export const resolveAvailableWithOB = (avail) =>
    avail?.AvailabilityConsideringOverbooking ?? avail?.Available;

const DayCell = React.memo(({ date, data, dateBuckets, eventSpans, allocationDateBuckets, state, allocationDates, allocationIds, text, message, currentHotel, availableMode, afterTax, eventColorMap, selected, onSelect }) => {
    // Fallback path: when DayCell is used without precomputed buckets (tests,
    // standalone consumers), build them locally. The parent in MonthlyView is
    // expected to pass dateBuckets/eventSpans/allocationDateBuckets from
    // useDayCellBuckets/useEventSpans/useAllocationDateBuckets so the work happens
    // once per data change rather than per cell.
    const localBuckets = useMemo(
        () => (dateBuckets ? null : buildDateBuckets(data)),
        [dateBuckets, data]
    );
    const buckets = dateBuckets || localBuckets;
    const localEventSpans = useMemo(
        () => (eventSpans ? null : buildEventSpans(data?.HotelParameters)),
        [eventSpans, data?.HotelParameters]
    );
    const spans = eventSpans || localEventSpans;
    const localAllocationDateBuckets = useMemo(
        () => (allocationDateBuckets ? null : buildAllocationDateBuckets(allocationDates, allocationIds)),
        [allocationDateBuckets, allocationDates, allocationIds]
    );
    const allocBuckets = allocationDateBuckets || localAllocationDateBuckets;

    const dateKey = isoKey(date);

    const Y = buckets.yieldByDate.get(dateKey);
    // OCCROOMS-1: mirror the server's AvailabilitySummary default (includeComplex=false)
    // and exclude IsComplex parent rows so we never double-count alongside their
    // non-complex children if the BFF payload happens to include both.
    const availabilityRows = buckets.availabilityByDate.get(dateKey);
    const availability = availabilityRows ? availabilityRows.filter(x => !x.IsComplex) : undefined;


    const avail = aggregateAvailability(availability);

    // Raw DTO inputs (validated below for the OCCROOMS-1 display)
    const rawSold = avail?.Sold;
    const rawAvailable = avail?.Available;
    const rawGI = avail?.GrossInventory;
    const rawOos = avail?.Oos;
    const rawOoo = avail?.Ooo;

    if (avail) {
        if (rawSold == null) console.warn(`Monthly View: Sold field missing for ${dateKey}`);
        if (rawAvailable == null) console.warn(`Monthly View: Available field missing for ${dateKey}`);
    }

    // OCCROOMS-1 derivations (FR-1..FR-5):
    //   Occupied = Sold (clamped to 0 if negative anomaly)
    //   Available = DTO.Available (negative when overbooked; OB shown separately, HS-70658)
    //   Denom = GrossInventory - Oos (Operational Inventory; clamped to 0 if negative)
    //   When Denom <= 0 (entire RT OOS) => numbers still render (HS-70658), tooltip flags "All rooms OOS"
    const Sold = rawSold ?? 0;
    const I = rawGI ?? 0;
    const Oos = rawOos ?? 0;
    if (rawSold != null && rawSold < 0) {
        console.warn(`Monthly View: negative Sold (${rawSold}) for ${dateKey}`);
    }
    if (Oos > I && rawGI != null) {
        console.warn(`Monthly View: Oos (${Oos}) exceeds GrossInventory (${I}) for ${dateKey}`);
    }
    const Denom = Math.max(0, I - Oos);
    // HS-70658: plain Available, NOT clamped — overbooked days show the real negative value.
    // The OB allowance is shown separately (tooltip line), never added to the cell number.
    const AvailableDisplay = rawAvailable ?? 0;
    const Overbooking = Math.max(0, (resolveAvailableWithOB(avail) ?? 0) - AvailableDisplay);
    const isAllOos = avail != null && Denom <= 0 && I > 0;
    // HS-70658: sold out or overbooked (Available <= 0) gets the red --full styling, regardless of OB.
    const isFull = rawAvailable != null && rawAvailable <= 0 && I > 0;
    const P = buckets.productsByDate.get(dateKey);
    const currentRestrictions = buckets.restrictionsByDate.get(dateKey);
    const ER = currentRestrictions?.EffectiveRestriction ?? RestrictionTypes.OpenRestrictionType;
    const currentReservationPolicy = buckets.reservationPoliciesByDate.get(dateKey);
    const policyDescription = currentReservationPolicy
        ? buildAutoDescriptionForPolicy(currentReservationPolicy, message, text, currentHotel?.HotelParameter?.TimeZone)
        : null;

    const relatedAllocationDates = allocBuckets.get(dateKey) || [];
    const allocationSums = relatedAllocationDates.reduce((acc, curr) => {
        acc.AllocatedRooms += curr.AllocatedRooms;
        acc.ReservedRooms += curr.ReservedRooms;
        acc.LeftRooms += curr.LeftRooms;
        acc.PresentedRooms += curr.PresentedRooms;
        return acc;
    }, { AllocatedRooms: 0, ReservedRooms: 0, LeftRooms: 0, PresentedRooms: 0 });

    const AL = relatedAllocationDates.length > 0 && {
        A: allocationSums.AllocatedRooms,
        U: allocationSums.ReservedRooms,
        L: allocationSums.LeftRooms,
        P: allocationSums.PresentedRooms,
    };

    const hotelParameter = buckets.hotelParameterByDate.get(dateKey);
    const notes = hotelParameter?.Notes;
    const event = hotelParameter?.CalendarEventId
        ? data?.CalendarEvents?.find(x => x.Id === hotelParameter.CalendarEventId)
        : null;
    const eventName = event?.IsActive ? event.Name : null;
    const eventId = hotelParameter?.CalendarEventId;

    // Determine event span position (first / middle / last / single) using the
    // adjacent-day buckets and the precomputed centerKey for this event id.
    let eventPosition = null;
    let isEventFirst = false;
    if (eventName && eventId) {
        const prevKey = moment(date).subtract(1, 'day').format('YYYY-MM-DD');
        const nextKey = moment(date).add(1, 'day').format('YYYY-MM-DD');
        const prevHasEvent = buckets.hotelParameterByDate.get(prevKey)?.CalendarEventId === eventId;
        const nextHasEvent = buckets.hotelParameterByDate.get(nextKey)?.CalendarEventId === eventId;

        if (prevHasEvent && nextHasEvent) eventPosition = 'middle';
        else if (prevHasEvent) eventPosition = 'last';
        else if (nextHasEvent) eventPosition = 'first';
        else eventPosition = 'single';

        if (eventPosition === 'single') {
            isEventFirst = true;
        } else {
            const span = spans.get(eventId);
            isEventFirst = span ? span.centerKey === dateKey : false;
        }
    }

    const forecastEntry = buckets.forecastByDate.get(dateKey);
    const forecastRooms = forecastEntry?.RoomQuantityForecast;
    const hotelAvailForDate = buckets.hotelAvailabilityGIByDate.has(dateKey)
        ? buckets.hotelAvailabilityGIByDate.get(dateKey)
        : I;
    // Prefer the user-edited OccupancyPercentForecast (HS-53710) when set; otherwise
    // derive from RoomQuantityForecast / hotelAvailability so we keep showing a value
    // for rows where only the legacy quantity column is populated.
    const forecastPercent = forecastEntry?.OccupancyPercentForecast != null
        ? forecastEntry.OccupancyPercentForecast
        : (forecastRooms != null && hotelAvailForDate > 0 ? (forecastRooms / hotelAvailForDate) * 100 : null);
    // text() renders the literal "[Forecast]" for a missing resource key, so a plain
    // `|| "Forecast"` fallback never fires (the string is truthy); guard explicitly.
    const forecastTextRaw = text("Forecast");
    const forecastLabel = (forecastTextRaw && !forecastTextRaw.startsWith("[")) ? forecastTextRaw : "Forecast";

    const minStay = currentRestrictions?.MinLengthOfStay;
    const maxStay = currentRestrictions?.MaxLengthOfStay;

    const restrictionBadgeColor = RESTRICTION_COLORS[RestrictionTypesValues[ER]?.color] || '#8bc34a';

    const calendarColor = eventColorMap && Object.hasOwn(eventColorMap, eventId) ? eventColorMap[eventId] : hotelParameter?.AvailabilityCalendarColor;
    const cellClass = `day-cell${selected ? ' day-cell--selected' : ''}${isFull ? ' day-cell--full' : ''}`;

    return (
        <div className={cellClass} onClick={() => onSelect?.(date)}>
            {/* Header: day number + policy + restriction/comment icons */}
            <div className="day-cell-header">
                <span className="day-number">{date.getDate()}</span>
                {/* Policy badge - centered between day number and icons */}
                {currentReservationPolicy && (
                    <Popup
                        trigger={
                            <span className="policy-badge">
                                <Icon name="sticky note outline" size="small" />
                                {currentReservationPolicy.Name}
                            </span>
                        }
                        content={
                            <div style={{ padding: 10 }}>
                                {policyDescription
                                    ? <TextList text={policyDescription} />
                                    : <span>{currentReservationPolicy.Name}</span>
                                }
                            </div>
                        }
                    />
                )}
                <div className="day-cell-icons">
                    {/* Restriction badge - filled when uniform, outline when not (FR-9) */}
                    <Popup
                        trigger={
                            <span
                                className={`restriction-badge${currentRestrictions?.IsUniform === false ? ' restriction-badge--other' : ''}`}
                                style={currentRestrictions?.IsUniform === false
                                    ? { borderColor: restrictionBadgeColor, color: restrictionBadgeColor }
                                    : { backgroundColor: restrictionBadgeColor }
                                }
                            >
                                {RestrictionTypesValues[ER]?.value}
                            </span>
                        }
                        content={text(RestrictionTypesValues[ER]?.key)}
                    />
                    {/* Notes icon */}
                    {notes != null && (
                        <Popup content={notes} trigger={<Icon name="comment" size="small" className="cell-action cell-active" />} />
                    )}
                </div>
            </div>

            {/* Availability center block - respects Available/Occupied toggle.
                OCCROOMS-1: format is "<n> out of <X>" where X = GrossInventory - Oos.
                - availableMode=true  -> Available numerator (incl. overbooking, negative when overbooked)
                - availableMode=false -> Occupied numerator (Sold)
                Denom is identical across both toggle states (FR-7/AC-7.2). */}
            <AvailabilityCenter
                availableMode={availableMode}
                AvailableDisplay={AvailableDisplay}
                Overbooking={Overbooking}
                Sold={Sold}
                Denom={Denom}
                isAllOos={isAllOos}
                I={I}
                Oos={Oos}
                rawOoo={rawOoo}
                text={text}
            />

            {/* Metrics row: Yield + Occupancy + Forecast */}
            <div className="day-cell-metrics">
                <Popup
                    trigger={
                        <span className="yield-badge">
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5 8L1.5 2H8.5L5 8Z" fill="#a142ff" />
                            </svg>
                            Y={Y?.Level || 0}
                        </span>
                    }
                    content={text("Yield")}
                />
                <Popup
                    trigger={<span className="occ-text">{formatOccupancy(Sold, Denom)}% {text("Occupancy") || "Occ."}</span>}
                    content={text("Occupancy")}
                />
                <Popup
                    trigger={<span className="forecast-text">{forecastPercent != null ? forecastPercent.toFixed(0) : 0}% {forecastLabel}</span>}
                    content={forecastLabel}
                />
            </div>

            {/* Min/Max nights row */}
            {(minStay > 1 || (maxStay > 0 && maxStay < 999)) && (
                <div className="day-cell-minstay">
                    <Icon name="moon outline" size="small" className="moon-icon" />
                    {minStay > 1 && (
                        <div className="stay-box">
                            <span className="stay-value">{minStay}</span>
                            <span className="stay-label">Min</span>
                        </div>
                    )}
                    {maxStay > 0 && maxStay < 999 && (
                        <div className="stay-box">
                            <span className="stay-value">{maxStay}</span>
                            <span className="stay-label">Max</span>
                        </div>
                    )}
                </div>
            )}

            {/* Prices section - only show when Rate Plan selected (FR-7) */}
            {state.RateId && P && (
                <div className="day-cell-prices">
                    <Popup
                        trigger={
                            <div className="price-row">
                                <span className="price-label">
                                    <Icon name="bed" size="small" />
                                    S
                                </span>
                                <span className="price-value">
                                    {formatValueAsCurrency(afterTax ? P.PricePerPaxAfterTax?.Single : P.PricePerPaxBeforeTax?.Single, P.CurrencyId)}
                                </span>
                            </div>
                        }
                        content={[P.RoomTypeName, P.RateName, P.Name].filter(Boolean).join(' / ')}
                    />
                    <Popup
                        trigger={
                            <div className="price-row">
                                <span className="price-label">
                                    <Icon name="bed" size="small" />
                                    D
                                </span>
                                <span className="price-value">
                                    {formatValueAsCurrency(afterTax ? P.PricePerPaxAfterTax?.Double : P.PricePerPaxBeforeTax?.Double, P.CurrencyId)}
                                </span>
                            </div>
                        }
                        content={[P.RoomTypeName, P.RateName, P.Name].filter(Boolean).join(' / ')}
                    />
                </div>
            )}

            {/* Allocation info */}
            {AL && (
                <div className="day-cell-allocations">
                    <Popup
                        trigger={
                            <div className="alloc-row">
                                <span className="alloc-label">A/U/L</span>
                                <span className="alloc-value">{AL.A || 0}/{AL.U || 0}/{AL.L || 0}</span>
                            </div>
                        }
                        content={
                            <div style={{ padding: "8px" }}>
                                Allocated: {AL.A || 0}<br />
                                Used: {AL.U || 0}<br />
                                Left: {AL.L || 0}<br />
                                Presented: {AL.P || 0}
                            </div>
                        }
                    />
                </div>
            )}

            {/* Event strip at bottom */}
            <div className={`day-cell-event ${eventName ? `event-${eventPosition}` : 'event-placeholder'}`}>
                {eventName && (
                    <Popup
                        trigger={<span className="event-name" style={calendarColor ? { backgroundColor: calendarColor, color: isLightColor(calendarColor) ? '#1a1a1a' : '#fff' } : {}}>{isEventFirst ? eventName : ' '}</span>}
                        content={eventName}
                    />
                )}
            </div>
        </div>
    );
});

export default DayCell;
